// Prism GraphQL client — the data source for the native Graph Explorer.
// Posts a single allowlisted read (sankeyData) to /api/prism/graphql, which the
// Go console reverse-proxies to the co-located CCPD Prism BFF (prism_proxy.go).
// Mirrors fleet.ts's raw-fetch + localStorage-bearer pattern rather than
// openapi-fetch, since Prism is a proxied GraphQL endpoint, not a typed Go route.
//
// The proxy enforces read-only: only queries selecting `sankeyData` pass; the
// query below is anonymous but selects it, so it clears the allowlist.

const BASE = import.meta.env.BASE_URL + "api";

function authHeaders(): Record<string, string> {
  const tok = localStorage.getItem("bluerock.token");
  return {
    "Content-Type": "application/json",
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
}

// The graph query. Field set matches CCPD's SANKEY_DATA_QUERY (verified live):
// per-node metadata + alert_originated, per-link session bundle. The greenfield
// model layer normalises this into a typed node/edge graph.
const SANKEY_QUERY = `query($start:DateTime!,$end:DateTime!,$filters:SankeyFilters){
  sankeyData(timeRange:{start:$start,end:$end}, filters:$filters){
    nodes{
      id label shortName type timestamp
      metadata{ entityId agentId hostId endpoint serverName sandboxId sandboxName sandboxRuntime execPath processName sha256 sandboxComponentId correlatedMcpServer correlatedMcpClient }
      alert_originated{ alert_id short_name alert_type severity origination_anchor_type origination_anchor_id alertCount message }
    }
    links{
      source target value sessionId sessionShortName timestamp createdAt terminatedAt status
      sessionBundle{ total active idle historical blocked violation
        sessions{ sessionId shortName status createdAt terminatedAt lastActivity requestCount } }
    }
    bluepythonAgents{ id component_id short_name hostid observed_user{ short_name user_id display_name } }
    observedUsers{ id user_id short_name display_name }
  }
}`;

export interface SankeyMeta {
  entityId?: string | null; agentId?: string | null; hostId?: string | null;
  endpoint?: string | null; serverName?: string | null; sandboxId?: string | null;
  sandboxName?: string | null; sandboxRuntime?: string | null; execPath?: string | null;
  processName?: string | null; sha256?: string | null; sandboxComponentId?: string | null;
  correlatedMcpServer?: string | null; correlatedMcpClient?: string | null;
}
export interface AlertOriginated {
  alert_id: string; short_name: string; alert_type: string; severity: string;
  origination_anchor_type: string; origination_anchor_id: string; alertCount: number;
  /** Human message the alert carried, e.g. "Blocked by BlueRock: <command>". */
  message?: string | null;
}
export interface SankeyNode {
  id: string; label: string; shortName: string; type: string; timestamp?: string | null;
  metadata?: SankeyMeta | null; alert_originated?: AlertOriginated[] | AlertOriginated | null;
}
export interface SessionBundle {
  total: number; active: number; idle: number; historical: number; blocked: number; violation: number;
  sessions?: Array<{ sessionId: string; shortName: string; status: string; createdAt?: string | null;
    terminatedAt?: string | null; lastActivity?: string | null; requestCount: number }>;
}
export interface SankeyLink {
  source: string; target: string; value: number; sessionId: string; sessionShortName?: string;
  status: string; createdAt?: string | null; terminatedAt?: string | null; sessionBundle?: SessionBundle | null;
}
export interface ObservedUserRef { short_name: string; user_id: string; display_name: string }
export interface BluepythonAgent {
  id: string; component_id?: string; short_name: string; hostid?: string;
  observed_user?: ObservedUserRef | null;
}
export interface ObservedUser { id: string; user_id: string; short_name: string; display_name: string }
export interface AiModel { id: string; short_name: string; name?: string; provider?: string }
export interface SankeyData {
  nodes: SankeyNode[];
  links: SankeyLink[];
  bluepythonAgents?: BluepythonAgent[];
  observedUsers?: ObservedUser[];
  aiModels?: AiModel[];
}

// --- Session trace (Phase 2.5 ladder) — Prism sessionFullTrace ---------------
// The per-session event timeline: LLM calls, tool/MCP calls, hook/permission
// events, brace/kernel events, and blocks, in order. Backs the trace panel.
const SESSION_TRACE_QUERY = `query($sessionId:String!,$includeBraceEvents:Boolean,$limit:Int,$llmOnly:Boolean){
  sessionFullTrace(sessionId:$sessionId, includeBraceEvents:$includeBraceEvents, limit:$limit, llmOnly:$llmOnly){
    sessionId
    totalEvents
    truncated
    events{
      eventId timestamp category eventName severity summary method mcpEvent toolName
      host hostShortName entityId entityShortName sensorId payload durationMs spanId
    }
  }
}`;

export interface TraceEvent {
  eventId: string;
  timestamp: string;
  category?: string | null;
  eventName?: string | null;
  severity?: string | null;
  summary?: string | null;
  method?: string | null;
  mcpEvent?: boolean | null;
  toolName?: string | null;
  host?: string | null;
  hostShortName?: string | null;
  entityId?: string | null;
  entityShortName?: string | null;
  sensorId?: string | null;
  /** JSON string — the event ARGUMENTS/payload shown in the detail drilldown. */
  payload?: string | null;
  durationMs?: number | null;
  spanId?: string | null;
}
export interface SessionTrace {
  sessionId: string;
  totalEvents: number;
  truncated: boolean;
  events: TraceEvent[];
}
export interface TraceOpts { includeBraceEvents?: boolean; limit?: number; llmOnly?: boolean }

interface GqlResponse<T> { data?: T; errors?: Array<{ message: string }>; }

async function gql<T>(query: string, variables: Record<string, unknown>): Promise<T> {
  const res = await fetch(`${BASE}/prism/graphql`, {
    method: "POST",
    cache: "no-store",
    headers: authHeaders(),
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) {
    let detail = `Prism request failed (${res.status})`;
    try {
      const b = (await res.json()) as { detail?: string; error?: string };
      detail = b.detail ?? b.error ?? detail;
    } catch { /* not JSON */ }
    throw new Error(detail);
  }
  const body = (await res.json()) as GqlResponse<T>;
  if (body.errors?.length) throw new Error(body.errors.map((e) => e.message).join("; "));
  if (!body.data) throw new Error("Prism returned no data");
  return body.data;
}

export const prismApi = {
  /** Fetch the graph for a time window. `start`/`end` are ISO-8601 strings. */
  sankeyData: (startISO: string, endISO: string, filters: Record<string, unknown> = {}) =>
    gql<{ sankeyData: SankeyData }>(SANKEY_QUERY, { start: startISO, end: endISO, filters }).then(
      (r) => r.sankeyData ?? { nodes: [], links: [] },
    ),
  /** Fetch the full event trace (ladder) for one session. */
  sessionTrace: (sessionId: string, opts: TraceOpts = {}) =>
    gql<{ sessionFullTrace: SessionTrace }>(SESSION_TRACE_QUERY, {
      sessionId,
      includeBraceEvents: opts.includeBraceEvents ?? true,
      limit: opts.limit ?? 500,
      llmOnly: opts.llmOnly ?? false,
    }).then((r) => r.sessionFullTrace ?? { sessionId, totalEvents: 0, truncated: false, events: [] }),
};
